import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { storage } from '../utils/storage';
import { db } from '../config/firebase';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  doc, 
  getDoc, 
  orderBy,
  Timestamp,
  updateDoc,
  serverTimestamp,
  deleteDoc,
  getDocs
} from 'firebase/firestore';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';

interface Chamado {
  id: string;
  empresaId: string;
  cliente: string;
  telefone: string;
  setor: string;
  status: 'pendente' | 'agendado' | 'aceita' | 'chegou_empresa' | 'em_rota' | 'chegou_destino' | 'entregue' | 'cancelado';
  criadoEm?: Timestamp;
  agendadoPara?: Timestamp | null;
  liberadoEm?: Timestamp | null;
  motoboyId?: string | null;
  motoboyNome?: string;
  entregueEm?: Timestamp | null;
  canceladoEm?: Timestamp | null;
  retirada?: string;
  destino?: string;
  frete?: number;
  taxaCentral?: number;
  numeroPedido?: number;
  numeroGlobal?: number;
  endereco?: { rua: string; numero: string };
  adicional?: string;
  codigoRastreamento?: string;
  grupoId?: string;
  apenasEntrega?: boolean;
  pagamentoPendente?: boolean;
  motivoCancelamento?: string;
  motivoCustomizado?: string;
}

interface Motoboy {
  id: string;
  nome: string;
  telefone: string;
  whatsapp?: string;
  fotoUsuarioUrl?: string;
}

interface ResumoPedidos {
  total: number;
  pendentes: number;
  emAndamento: number;
  entregues: number;
  cancelados: number;
  valorTotal: number;
}

// Função utilitária para formatar tempo decorrido
function formatElapsedTime(date: Date, currentTime: Date) {
  const diff = Math.floor((currentTime.getTime() - date.getTime()) / 1000);
  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60).toString().padStart(2, '0');
  const sec = (diff % 60).toString().padStart(2, '0');
  const isOver50Min = diff > 3000;
  
  let timeString;
  if (hours > 0) {
    timeString = `${hours}h ${minutes}:${sec}`;
  } else {
    timeString = `${minutes}:${sec}`;
  }

  return { time: timeString, isOver50Min };
}

const formatarMoeda = (valor: number | undefined): string => {
  if (valor === undefined) return '0,00';
  return valor.toFixed(2).replace('.', ',');
};

const calcularValorFinal = (chamado: Chamado): number => {
  const frete = Number(chamado?.frete || 0);
  const taxa = Number(chamado?.taxaCentral || 0);
  return frete + taxa;
};

// Remover duplicatas de pedidos
const removerDuplicatasPedidos = (todosChamados: Chamado[], entregues: Chamado[], canceladas: Chamado[] = []): Chamado[] => {
  const pedidosMap = new Map<string, Chamado>();
  
  entregues.forEach(item => {
    if (item.id) {
      pedidosMap.set(item.id, item);
    }
  });
  
  canceladas.forEach(item => {
    if (item.id && !pedidosMap.has(item.id)) {
      pedidosMap.set(item.id, item);
    }
  });
  
  todosChamados.forEach(item => {
    if (item.id && !pedidosMap.has(item.id)) {
      pedidosMap.set(item.id, item);
    }
  });
  
  return Array.from(pedidosMap.values());
};

const StatusPedido: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const chamadoIdParam = searchParams.get('chamadoId');
  const { userData } = useAuth();
  
  const [todosChamados, setTodosChamados] = useState<Chamado[]>([]);
  const [entregues, setEntregues] = useState<Chamado[]>([]);
  const [entregasCanceladas, setEntregasCanceladas] = useState<Chamado[]>([]);
  const [empresaId, setEmpresaId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFiltro, setStatusFiltro] = useState<'todos' | 'pendente' | 'agendado' | 'aceita' | 'em_rota' | 'entregue' | 'cancelado'>('todos');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [resumoPedidos, setResumoPedidos] = useState<ResumoPedidos>({
    total: 0,
    pendentes: 0,
    emAndamento: 0,
    entregues: 0,
    cancelados: 0,
    valorTotal: 0
  });
  const [motoboyCache, setMotoboyCache] = useState<{[key: string]: Motoboy}>({});
  const [showDetalhesModal, setShowDetalhesModal] = useState(false);
  const [chamadoSelecionado, setChamadoSelecionado] = useState<Chamado | null>(null);
  const [empresaNome, setEmpresaNome] = useState<string>('');
  const [empresaFoto, setEmpresaFoto] = useState<string>('');
  const [showConfirmarChamar, setShowConfirmarChamar] = useState(false);
  const [chamadoParaChamar, setChamadoParaChamar] = useState<Chamado | null>(null);
  const [isChamando, setIsChamando] = useState(false);
  const [showConfirmarCancelar, setShowConfirmarCancelar] = useState(false);
  const [chamadoParaCancelar, setChamadoParaCancelar] = useState<Chamado | null>(null);
  const [isCancelando, setIsCancelando] = useState(false);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');
  const [fotoExpandida, setFotoExpandida] = useState<{ url: string; nome: string } | null>(null);
  const { logout } = useAuth();

  // Atualiza currentTime a cada 1s
  useEffect(() => {
    const id = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Carregar empresaId e dados da empresa
  useEffect(() => {
    const fetchEmpresaId = async () => {
      try {
        const userId = await storage.getItem('userId');
        if (userId) {
          const usuarioRef = doc(db, 'usuarios', userId);
          const usuarioDoc = await getDoc(usuarioRef);
          
          if (usuarioDoc.exists()) {
            const usuarioData = usuarioDoc.data();
            const empresaId = usuarioData.empresaId;
            
            if (empresaId) {
              setEmpresaId(empresaId);
              
              // Buscar dados da empresa
              const empresaRef = doc(db, 'Empresa', empresaId);
              const empresaDoc = await getDoc(empresaRef);
              
              if (empresaDoc.exists()) {
                const empresaData = empresaDoc.data();
                setEmpresaNome(empresaData.nomeEmpresa || empresaData.nome || 'Empresa');
                setEmpresaFoto(empresaData.foto || empresaData.fotoUrl || '');
              }
            }
          }
        }
      } catch (error) {
        console.error('Erro ao buscar ID da empresa:', error);
      }
    };
    
    fetchEmpresaId();
  }, []);

  // Handle logout
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Carregar pedidos em tempo real
  useEffect(() => {
    if (!empresaId) return;

    setLoading(true);
    
    // Busca na coleção chamados
    const qChamados = query(
      collection(db, 'chamados1'),
      where('empresaId', '==', empresaId),
      where('status', 'in', ['pendente', 'agendado', 'aceita', 'chegou_empresa', 'em_rota', 'chegou_destino', 'entregue', 'cancelado'])
    );

    const unsubscribeChamados = onSnapshot(qChamados, async (snapshot) => {
      try {
        const chamados = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Chamado[];
        
        setTodosChamados(chamados);
        setLoading(false);
      } catch (error: any) {
        console.error('Erro ao processar snapshot de chamados:', error);
        setLoading(false);
      }
    });

    // Busca na subcoleção entregues
    const empresaRef = doc(db, 'Empresa', empresaId);
    const qEntregues = query(
      collection(empresaRef, 'entregues'),
      orderBy('entregueEm', 'desc')
    );

    const unsubscribeEntregues = onSnapshot(qEntregues, (snapshot) => {
      const entregues = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Chamado[];
      setEntregues(entregues);
    });

    // Busca na subcoleção entregas_canceladas (SOMENTE DO DIA)
    const hoje = new Date();
    const inicioDia = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
    const fimDia = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 23, 59, 59, 999);
    
    const inicioTimestamp = Timestamp.fromDate(inicioDia);
    const fimTimestamp = Timestamp.fromDate(fimDia);
    
    const qCanceladas = query(
      collection(empresaRef, 'entregas_canceladas'),
      where('canceladoEm', '>=', inicioTimestamp),
      where('canceladoEm', '<=', fimTimestamp),
      orderBy('canceladoEm', 'desc')
    );

    const unsubscribeCanceladas = onSnapshot(qCanceladas, (snapshot) => {
      const canceladas = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Chamado[];
      setEntregasCanceladas(canceladas);
    });

    return () => {
      unsubscribeChamados();
      unsubscribeEntregues();
      unsubscribeCanceladas();
    };
  }, [empresaId]);

  // Carregar informações dos motoboys
  useEffect(() => {
    const pedidosUnicos = removerDuplicatasPedidos(todosChamados, entregues, entregasCanceladas);
    const motoboyIds = pedidosUnicos
      .filter(pedido => pedido.motoboyId && !motoboyCache[pedido.motoboyId])
      .map(pedido => pedido.motoboyId as string);

    motoboyIds.forEach(motoboyId => {
      if (!motoboyCache[motoboyId]) {
        buscarMotoboyInfo(motoboyId);
      }
    });
  }, [todosChamados, entregues, motoboyCache]);

  const buscarMotoboyInfo = async (motoboyId: string) => {
    try {
      if (motoboyCache[motoboyId]) return;

      const motoboyRef = doc(db, 'usuarios', motoboyId);
      const motoboyDoc = await getDoc(motoboyRef);
      
      if (motoboyDoc.exists()) {
        const motoboyData = {
          id: motoboyDoc.id,
          ...motoboyDoc.data()
        } as Motoboy;
        
        setMotoboyCache(prev => ({
          ...prev,
          [motoboyId]: motoboyData
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar dados do motoboy:', error);
    }
  };

  // Calcular resumo
  useEffect(() => {
    const pedidosUnicos = removerDuplicatasPedidos(todosChamados, entregues, entregasCanceladas);
    
    if (pedidosUnicos.length > 0) {
      const resumo = pedidosUnicos.reduce((acc, chamado) => {
        if (chamado.status !== 'cancelado') {
          acc.total++;
          acc.valorTotal += calcularValorFinal(chamado);
        }

        switch(chamado.status) {
          case 'pendente':
          case 'agendado':
            acc.pendentes++;
            break;
          case 'entregue':
            acc.entregues++;
            break;
          case 'cancelado':
            acc.cancelados++;
            break;
          default:
            acc.emAndamento++;
        }

        return acc;
      }, {
        total: 0,
        pendentes: 0,
        emAndamento: 0,
        entregues: 0,
        cancelados: 0,
        valorTotal: 0
      });

      setResumoPedidos(resumo);
    }
  }, [todosChamados, entregues, entregasCanceladas]);

  // Filtros e busca
  const chamadosFiltrados = useMemo(() => {
    const pedidosUnicos = removerDuplicatasPedidos(todosChamados, entregues, entregasCanceladas);
    
    return pedidosUnicos
      .filter((item) => {
        // Filtro de status
        let statusOk = statusFiltro === 'todos';
        if (!statusOk) {
          if (statusFiltro === 'em_rota') {
            statusOk = item.status === 'pendente' || item.status === 'em_rota';
          } else if (statusFiltro === 'aceita') {
            statusOk = item.status === 'aceita';
          } else if (statusFiltro === 'entregue') {
            statusOk = item.status === 'entregue' || entregues.some(e => e.id === item.id);
          } else if (statusFiltro === 'cancelado') {
            statusOk = item.status === 'cancelado';
          } else {
            statusOk = item.status === statusFiltro;
          }
        }
        
        // Filtro de busca
        const busca = search.trim().toLowerCase();
        let buscaOk = true;
        if (busca.length > 0) {
          buscaOk = !!(
            (item.numeroPedido && `#${item.numeroPedido}`.includes(busca)) ||
            (item.cliente && item.cliente.toLowerCase().includes(busca)) ||
            (item.setor && item.setor.toLowerCase().includes(busca))
          );
        }
        return statusOk && buscaOk;
      })
      .sort((a, b) => {
        const dataA = a.criadoEm?.toDate() || new Date(0);
        const dataB = b.criadoEm?.toDate() || new Date(0);
        return dataB.getTime() - dataA.getTime();
      });
  }, [todosChamados, entregues, entregasCanceladas, search, statusFiltro]);

  // Opções de status
  const statusOptions = useMemo(() => {
    const pedidosUnicos = removerDuplicatasPedidos(todosChamados, entregues, entregasCanceladas);
    
    const counts = {
      todos: pedidosUnicos.length,
      pendente: pedidosUnicos.filter(c => c.status === 'pendente').length,
      agendado: pedidosUnicos.filter(c => c.status === 'agendado').length,
      aceita: pedidosUnicos.filter(c => c.status === 'aceita').length,
      em_rota: pedidosUnicos.filter(c => c.status === 'em_rota').length,
      entregue: pedidosUnicos.filter(c => c.status === 'entregue').length,
      cancelado: pedidosUnicos.filter(c => c.status === 'cancelado').length
    };

    return [
      { key: 'todos', label: `Todos (${counts.todos})`, color: '#1976d2' },
      { key: 'pendente', label: `Pendente (${counts.pendente})`, color: '#FFA500' },
      { key: 'agendado', label: `Agendado (${counts.agendado})`, color: '#9C27B0' },
      { key: 'aceita', label: `Aceitos (${counts.aceita})`, color: '#1976d2' },
      { key: 'em_rota', label: `Em rota (${counts.em_rota})`, color: '#17a2b8' },
      { key: 'entregue', label: `Entregue (${counts.entregue})`, color: '#28a745' },
      { key: 'cancelado', label: `Cancelado (${counts.cancelado})`, color: '#dc3545' }
    ];
  }, [todosChamados, entregues, entregasCanceladas]);

  // Abrir detalhes do pedido
  const handleAbrirDetalhes = (chamado: Chamado) => {
    setChamadoSelecionado(chamado);
    setShowDetalhesModal(true);
  };

  // Abrir modal de confirmação para chamar pedido agendado
  const handleChamarAgora = (chamado: Chamado, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevenir que o clique abra os detalhes
    
    if (!chamado.id) return;
    
    setChamadoParaChamar(chamado);
    setShowConfirmarChamar(true);
  };

  // Confirmar e executar chamada do pedido
  const confirmarChamarAgora = async () => {
    if (!chamadoParaChamar?.id) return;

    setIsChamando(true);
    
    try {
      const chamadoRef = doc(db, 'chamados1', chamadoParaChamar.id);
      
      await updateDoc(chamadoRef, {
        status: 'pendente',
        liberadoEm: serverTimestamp(),
        agendadoPara: null
      });

      setShowConfirmarChamar(false);
      setChamadoParaChamar(null);
      
      // Mostrar mensagem de sucesso
      const sucessoDiv = document.createElement('div');
      sucessoDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-4 rounded-xl shadow-2xl z-[100] flex items-center gap-3 animate-slide-in';
      sucessoDiv.innerHTML = `
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <div>
          <p class="font-bold">Pedido chamado com sucesso!</p>
          <p class="text-sm text-green-100">Agora está disponível para os motoboys.</p>
        </div>
      `;
      document.body.appendChild(sucessoDiv);
      
      setTimeout(() => {
        sucessoDiv.style.animation = 'slide-out 0.3s ease-out';
        setTimeout(() => {
          document.body.removeChild(sucessoDiv);
        }, 300);
      }, 3000);
    } catch (error) {
      console.error('Erro ao chamar pedido:', error);
      alert('Erro ao chamar pedido. Tente novamente.');
    } finally {
      setIsChamando(false);
    }
  };

  // Abrir modal de confirmação para cancelar pedido
  const handleCancelarPedido = (chamado: Chamado, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevenir que o clique abra os detalhes
    
    if (!chamado.id) return;
    
    setChamadoParaCancelar(chamado);
    setMotivoCancelamento('');
    setShowConfirmarCancelar(true);
  };

  // Confirmar e executar cancelamento do pedido
  const confirmarCancelarPedido = async () => {
    if (!chamadoParaCancelar?.id) return;

    setIsCancelando(true);
    
    try {
      const chamadoRef = doc(db, 'chamados1', chamadoParaCancelar.id);
      
      await updateDoc(chamadoRef, {
        status: 'cancelado',
        canceladoEm: serverTimestamp(),
        motivoCancelamento: motivoCancelamento.trim() || 'Cancelado pela empresa'
      });

      setShowConfirmarCancelar(false);
      setChamadoParaCancelar(null);
      setMotivoCancelamento('');
      
      // Mostrar mensagem de sucesso
      const sucessoDiv = document.createElement('div');
      sucessoDiv.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-4 rounded-xl shadow-2xl z-[100] flex items-center gap-3 animate-slide-in';
      sucessoDiv.innerHTML = `
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <div>
          <p class="font-bold">Pedido cancelado com sucesso!</p>
          <p class="text-sm text-red-100">O pedido foi removido da lista de disponíveis.</p>
        </div>
      `;
      document.body.appendChild(sucessoDiv);
      
      setTimeout(() => {
        sucessoDiv.style.animation = 'slide-out 0.3s ease-out';
        setTimeout(() => {
          document.body.removeChild(sucessoDiv);
        }, 300);
      }, 3000);
    } catch (error) {
      console.error('Erro ao cancelar pedido:', error);
      alert('Erro ao cancelar pedido. Tente novamente.');
    } finally {
      setIsCancelando(false);
    }
  };

  // Renderizar card de pedido
  const renderChamadoCard = (item: Chamado) => {
    const isEntregue = entregues.some(e => e.id === item.id) || item.status === 'entregue';
    const isCancelado = item.status === 'cancelado';
    
    let statusLabel = '';
    let statusColor = '#FFA500';
    let statusBg = '#FFF8E1';
    
    if (isEntregue) {
      statusLabel = 'Entregue';
      statusColor = '#28a745';
      statusBg = '#E8F5E9';
    } else if (isCancelado) {
      statusLabel = 'Cancelado';
      statusColor = '#dc3545';
      statusBg = '#FFEBEE';
    } else {
      switch(item.status) {
        case 'pendente':
          statusLabel = 'Pendente';
          statusColor = '#FFA500';
          statusBg = '#FFF8E1';
          break;
        case 'agendado':
          statusLabel = 'Agendado';
          statusColor = '#9C27B0';
          statusBg = '#F3E5F5';
          break;
        case 'aceita':
        case 'chegou_empresa':
        case 'em_rota':
        case 'chegou_destino':
          statusLabel = 'Em andamento';
          statusColor = '#1976d2';
          statusBg = '#E3F2FD';
          break;
        default:
          statusLabel = item.status;
      }
    }

    const dataCriacao = item.criadoEm?.toDate() || new Date();
    const dataHora = format(dataCriacao, "dd/MM 'às' HH:mm", { locale: ptBR });
    
    const tempoInfo = formatElapsedTime(dataCriacao, currentTime);
    const tempoDecorrido = tempoInfo.time;
    const isOver50Min = tempoInfo.isOver50Min;

    const valorTotal = calcularValorFinal(item);
    const motoboyInfo = item.motoboyId ? motoboyCache[item.motoboyId] : null;

    return (
      <div
        key={item.id}
        onClick={() => handleAbrirDetalhes(item)}
        className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 p-4 mb-4 cursor-pointer border-l-4"
        style={{ borderLeftColor: statusColor }}
      >
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-bold text-gray-800">
                Pedido #{item.numeroPedido || 'N/A'}
              </h3>
              <span
                className="px-2 py-1 rounded-full text-xs font-semibold"
                style={{ backgroundColor: statusBg, color: statusColor }}
              >
                {statusLabel}
              </span>
            </div>
            <p className="text-gray-700 font-medium">{item.cliente}</p>
            <p className="text-sm text-gray-500">{item.setor}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">{dataHora}</p>
            <div className="flex items-center gap-1 mt-1">
              <span
                className={`text-xs font-medium ${
                  isOver50Min ? 'text-red-500' : 'text-green-600'
                }`}
              >
                {tempoDecorrido}
              </span>
            </div>
          </div>
        </div>

        {isEntregue && item.motoboyId && (
          <div className="flex items-center gap-2 mb-2 text-sm text-green-600">
            {motoboyInfo?.fotoUsuarioUrl ? (
              <img
                src={motoboyInfo.fotoUsuarioUrl}
                alt={motoboyInfo.nome || 'Motoboy'}
                onClick={(e) => {
                  e.stopPropagation();
                  setFotoExpandida({ url: motoboyInfo.fotoUsuarioUrl!, nome: motoboyInfo.nome || item.motoboyNome || 'Motoboy' });
                }}
                className="w-8 h-8 rounded-full object-cover border-2 border-green-200 cursor-pointer hover:scale-110 transition-transform"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-green-100 border-2 border-green-200 flex items-center justify-center">
                <span className="text-green-600 font-semibold text-xs">
                  {(motoboyInfo?.nome || item.motoboyNome || 'M')[0].toUpperCase()}
                </span>
              </div>
            )}
            <span>✓</span>
            <span>Entregue por: {motoboyInfo?.nome || item.motoboyNome || 'Carregando...'}</span>
          </div>
        )}

        {isCancelado && (
          <div className="flex items-center gap-2 mb-2 text-sm text-red-600">
            <span>✕</span>
            <span>Pedido cancelado</span>
            {item.motivoCancelamento && (
              <span className="text-xs text-gray-500">({item.motivoCancelamento})</span>
            )}
          </div>
        )}

        {!isEntregue && !isCancelado && item.motoboyId && (
          <div className="flex items-center gap-2 mb-2 text-sm text-blue-600">
            {motoboyInfo?.fotoUsuarioUrl ? (
              <img
                src={motoboyInfo.fotoUsuarioUrl}
                alt={motoboyInfo.nome || 'Motoboy'}
                onClick={(e) => {
                  e.stopPropagation();
                  setFotoExpandida({ url: motoboyInfo.fotoUsuarioUrl!, nome: motoboyInfo.nome || item.motoboyNome || 'Motoboy' });
                }}
                className="w-8 h-8 rounded-full object-cover border-2 border-blue-200 cursor-pointer hover:scale-110 transition-transform"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-100 border-2 border-blue-200 flex items-center justify-center">
                <span className="text-blue-600 font-semibold text-xs">
                  {(motoboyInfo?.nome || item.motoboyNome || 'M')[0].toUpperCase()}
                </span>
              </div>
            )}
            <span>🏍️</span>
            <span>{motoboyInfo?.nome || item.motoboyNome || 'Carregando...'}</span>
          </div>
        )}

        {!isEntregue && !isCancelado && !item.motoboyId && (
          <div className="flex items-center gap-2 mb-2 text-sm text-orange-500">
            <span>⏳</span>
            <span>Aguardando motoboy...</span>
          </div>
        )}

        <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center gap-2">
            {item.codigoRastreamento && (
              <span className="text-xs text-blue-600 font-medium">
                DVS-{item.numeroGlobal}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {item.status === 'agendado' && (
              <button
                onClick={(e) => handleChamarAgora(item, e)}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white text-sm font-semibold rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Chamar Agora
              </button>
            )}
            {!isEntregue && !isCancelado && !item.motoboyId && (item.status === 'pendente' || item.status === 'agendado') && (
              <button
                onClick={(e) => handleCancelarPedido(item, e)}
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white text-sm font-semibold rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Cancelar
              </button>
            )}
            <div className="text-right">
              <p className="text-lg font-bold text-gray-800">
                R$ {formatarMoeda(valorTotal)}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando pedidos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex">
      {/* Menu Lateral - Sempre Visível */}
      <div className="w-80 bg-white/95 backdrop-blur-xl shadow-2xl border-r border-gray-200/50 flex-shrink-0">
        <div className="p-6 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">Menu</h2>
          </div>
          <div className="flex flex-col items-center">
            {empresaFoto ? (
              <img
                src={empresaFoto}
                alt={empresaNome}
                className="w-20 h-20 rounded-2xl border-4 border-white/30 shadow-xl mb-3 object-cover"
              />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm border-4 border-white/30 shadow-xl mb-3 flex items-center justify-center">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            )}
            <p className="text-lg font-bold">{empresaNome || 'Empresa'}</p>
            <p className="text-sm text-blue-100 mt-1">Painel Administrativo</p>
          </div>
        </div>

        <nav className="p-4 space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          <button
            onClick={() => navigate('/empresa')}
            className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <span className="font-semibold text-gray-700">Dashboard</span>
          </button>

          <button
            onClick={() => navigate('/status-pedido')}
            className="w-full text-left px-4 py-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 transition-all flex items-center gap-4 group"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <span className="font-semibold text-indigo-700">Pedidos</span>
          </button>

          <button
            onClick={() => navigate('/perfil')}
            className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <span className="font-semibold text-gray-700">Perfil</span>
          </button>

          <button
            onClick={() => navigate('/clientes')}
            className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <span className="font-semibold text-gray-700">Lista de Clientes</span>
          </button>

          <button
            onClick={() => navigate('/relatorio')}
            className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <span className="font-semibold text-gray-700">Histórico de Entregas</span>
          </button>

          <button
            onClick={() => navigate('/tabela-taxas')}
            className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <span className="font-semibold text-gray-700">Tabela de Taxas</span>
          </button>

          <button
            onClick={() => navigate('/configuracoes')}
            className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all flex items-center gap-4 group"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <span className="font-semibold text-gray-700">Configurações</span>
          </button>

          <div className="pt-4 mt-4 border-t border-gray-200">
            <button
              onClick={() => {
                handleLogout();
              }}
              className="w-full text-left px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 transition-all flex items-center gap-4 group"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              <span className="font-semibold text-red-600">Sair</span>
            </button>
          </div>
        </nav>
      </div>

      {/* Conteúdo Principal */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-md shadow-md sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <img 
                  src="/logoheader1.png" 
                  alt="Rota Entregas" 
                  className="h-12 w-auto object-contain"
                />
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">Pedidos</h1>
                  <p className="text-sm text-gray-600">
                    {format(new Date(), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/empresa')}
                className="text-blue-600 hover:text-blue-800 flex items-center gap-2 px-4 py-2 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <span>←</span>
                <span>Voltar</span>
              </button>
            </div>

          {/* Estatísticas */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-4 text-white">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm opacity-90">Total</span>
                <span className="text-2xl">📦</span>
              </div>
              <p className="text-2xl font-bold">{resumoPedidos.total}</p>
              <p className="text-xs opacity-90">pedidos hoje</p>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-4 text-white">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm opacity-90">Valor</span>
                <span className="text-2xl">💰</span>
              </div>
              <p className="text-2xl font-bold">R$ {formatarMoeda(resumoPedidos.valorTotal)}</p>
              <p className="text-xs opacity-90">total hoje</p>
            </div>
          </div>

          {/* Busca */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Buscar por cliente, setor ou número do pedido..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filtros de status */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {statusOptions.map((option) => (
              <button
                key={option.key}
                onClick={() => setStatusFiltro(option.key as any)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  statusFiltro === option.key
                    ? 'text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
                style={
                  statusFiltro === option.key
                    ? { backgroundColor: option.color }
                    : {}
                }
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        </div>

        {/* Lista de Pedidos */}
        <div className="flex-1 max-w-7xl mx-auto px-4 py-6 w-full">
          {chamadosFiltrados.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📦</div>
              <p className="text-gray-600 text-lg">Nenhum pedido encontrado</p>
            </div>
          ) : (
            <div>
              {chamadosFiltrados.map(renderChamadoCard)}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Detalhes */}
      {showDetalhesModal && chamadoSelecionado && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  Pedido #{chamadoSelecionado.numeroPedido}
                </h2>
                <p className="text-sm text-gray-600">{chamadoSelecionado.cliente}</p>
              </div>
              <button
                onClick={() => setShowDetalhesModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Data</p>
                  <p className="font-medium">
                    {chamadoSelecionado.criadoEm?.toDate().toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Valor</p>
                  <p className="font-bold text-green-600">
                    R$ {formatarMoeda(calcularValorFinal(chamadoSelecionado))}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Cliente</p>
                <p className="font-medium">{chamadoSelecionado.cliente}</p>
                <p className="text-sm text-gray-600">{chamadoSelecionado.telefone}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-1">Endereço</p>
                <p className="font-medium">{chamadoSelecionado.setor}</p>
                {chamadoSelecionado.endereco?.rua && (
                  <p className="text-sm text-gray-600">
                    {chamadoSelecionado.endereco.rua}, {chamadoSelecionado.endereco.numero}
                  </p>
                )}
              </div>

              {chamadoSelecionado.motoboyId && (() => {
                const motoboyInfo = chamadoSelecionado.motoboyId ? motoboyCache[chamadoSelecionado.motoboyId] : null;
                return (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Entregador</p>
                    <div className="flex items-center gap-3">
                      {motoboyInfo?.fotoUsuarioUrl ? (
                        <img
                          src={motoboyInfo.fotoUsuarioUrl}
                          alt={motoboyInfo.nome || 'Motoboy'}
                          onClick={() => {
                            setFotoExpandida({ 
                              url: motoboyInfo.fotoUsuarioUrl!, 
                              nome: motoboyInfo.nome || chamadoSelecionado.motoboyNome || 'Motoboy' 
                            });
                          }}
                          className="w-12 h-12 rounded-full object-cover border-2 border-blue-200 cursor-pointer hover:scale-110 transition-transform"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-blue-100 border-2 border-blue-200 flex items-center justify-center">
                          <span className="text-blue-600 font-semibold text-lg">
                            {(motoboyInfo?.nome || chamadoSelecionado.motoboyNome || 'M')[0].toUpperCase()}
                          </span>
                        </div>
                      )}
                      <p className="font-medium">
                        {motoboyInfo?.nome || chamadoSelecionado.motoboyNome || 'Carregando...'}
                      </p>
                    </div>
                  </div>
                );
              })()}

              {chamadoSelecionado.adicional && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Observações</p>
                  <p className="text-gray-700">{chamadoSelecionado.adicional}</p>
                </div>
              )}

              {chamadoSelecionado.codigoRastreamento && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Código de Rastreamento</p>
                  <p className="font-mono text-blue-600">DVS-{chamadoSelecionado.numeroGlobal}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação - Chamar Agora */}
      {showConfirmarChamar && chamadoParaChamar && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">Chamar Pedido Agora?</h3>
                  <p className="text-sm text-gray-600 mt-1">Este pedido está agendado e será liberado imediatamente.</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 mb-6 border border-purple-200">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Pedido:</span>
                    <span className="text-sm font-bold text-gray-900">#{chamadoParaChamar.numeroPedido || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Cliente:</span>
                    <span className="text-sm font-semibold text-gray-900">{chamadoParaChamar.cliente}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Setor:</span>
                    <span className="text-sm font-semibold text-gray-900">{chamadoParaChamar.setor}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-purple-200">
                    <span className="text-sm font-medium text-gray-600">Valor:</span>
                    <span className="text-lg font-bold text-purple-600">
                      R$ {formatarMoeda(calcularValorFinal(chamadoParaChamar))}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-blue-800">
                    O pedido será alterado de <strong>Agendado</strong> para <strong>Pendente</strong> e ficará disponível imediatamente para os motoboys aceitarem.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowConfirmarChamar(false);
                    setChamadoParaChamar(null);
                  }}
                  disabled={isChamando}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmarChamarAgora}
                  disabled={isChamando}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-purple-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isChamando ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Chamando...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Sim, Chamar Agora
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação - Cancelar Pedido */}
      {showConfirmarCancelar && chamadoParaCancelar && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full transform transition-all">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">Cancelar Pedido?</h3>
                  <p className="text-sm text-gray-600 mt-1">Esta ação não pode ser desfeita.</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-4 mb-6 border border-red-200">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Pedido:</span>
                    <span className="text-sm font-bold text-gray-900">#{chamadoParaCancelar.numeroPedido || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Cliente:</span>
                    <span className="text-sm font-semibold text-gray-900">{chamadoParaCancelar.cliente}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Setor:</span>
                    <span className="text-sm font-semibold text-gray-900">{chamadoParaCancelar.setor}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-red-200">
                    <span className="text-sm font-medium text-gray-600">Valor:</span>
                    <span className="text-lg font-bold text-red-600">
                      R$ {formatarMoeda(calcularValorFinal(chamadoParaCancelar))}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motivo do Cancelamento (opcional)
                </label>
                <textarea
                  value={motivoCancelamento}
                  onChange={(e) => setMotivoCancelamento(e.target.value)}
                  placeholder="Ex: Cliente desistiu, erro no pedido, etc."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  rows={3}
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-sm text-yellow-800">
                    <strong>Atenção:</strong> O pedido será cancelado permanentemente e não poderá ser recuperado. Esta ação é irreversível.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowConfirmarCancelar(false);
                    setChamadoParaCancelar(null);
                    setMotivoCancelamento('');
                  }}
                  disabled={isCancelando}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Voltar
                </button>
                <button
                  onClick={confirmarCancelarPedido}
                  disabled={isCancelando}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-semibold hover:from-red-700 hover:to-red-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isCancelando ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Cancelando...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Sim, Cancelar Pedido
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Foto Expandida */}
      {fotoExpandida && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
          onClick={() => setFotoExpandida(null)}
        >
          <div 
            className="relative max-w-4xl w-full max-h-[90vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setFotoExpandida(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors p-2"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full">
              <div className="p-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                <h3 className="text-xl font-bold text-center">{fotoExpandida.nome}</h3>
              </div>
              <div className="p-4 flex items-center justify-center bg-gray-100">
                <img
                  src={fotoExpandida.url}
                  alt={fotoExpandida.nome}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StatusPedido;

